(() => {
  const moneyFormat = window.ObjectParty?.moneyFormat || "${{amount}}";
  const root = window.ObjectParty?.routes?.root || "/";
  const strings = window.ObjectParty?.strings || {};

  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    thousands = thousands || ",";
    decimal = decimal || ".";
    if (Number.isNaN(number) || number == null) return "0";
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? dollars + decimal + parts[1] : dollars;
  };

  const formatMoney = (cents, format = moneyFormat) => {
    const amount = typeof cents === "string" ? cents.replace(".", "") : cents;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : "amount";
    const formats = {
      amount: (value) => formatWithDelimiters(value, 2),
      amount_no_decimals: (value) => formatWithDelimiters(value, 0),
      amount_with_comma_separator: (value) => formatWithDelimiters(value, 2, ".", ","),
      amount_no_decimals_with_comma_separator: (value) => formatWithDelimiters(value, 0, ".", ","),
      amount_with_apostrophe_separator: (value) => formatWithDelimiters(value, 2, "'", "."),
      amount_with_space_separator: (value) => formatWithDelimiters(value, 2, " ", ","),
      amount_no_decimals_with_space_separator: (value) => formatWithDelimiters(value, 0, " "),
      amount_with_period_and_space_separator: (value) => formatWithDelimiters(value, 2, " ", ".")
    };
    const value = (formats[key] || formats.amount)(Number(amount));
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  const toast = (message) => {
    const el = document.querySelector("[data-cart-toast]");
    if (!el || !message) return;
    el.hidden = false;
    el.textContent = message;
    window.clearTimeout(toast._timer);
    toast._timer = window.setTimeout(() => {
      el.hidden = true;
    }, 3200);
  };

  const setInert = (on) => {
    document.body.classList.toggle("lock", on);
    ["MainContent", "SiteFooter"].forEach((id) => {
      const node = document.getElementById(id);
      if (node) node.toggleAttribute("inert", on);
    });
  };

  const cartRoot = () => document.querySelector("[data-cart-root]");
  const searchRoot = () => document.querySelector("[data-search-root]");
  const predictiveUrl = window.ObjectParty?.routes?.predictiveSearch || `${root}search/suggest`;
  const searchPageUrl = window.ObjectParty?.routes?.search || `${root}search`;

  const isOverlayOpen = () => {
    const menu = document.querySelector("[data-mobile-nav]");
    return (
      cartRoot()?.classList.contains("is-open") ||
      searchRoot()?.classList.contains("is-open") ||
      (menu && !menu.hidden)
    );
  };

  const setCartOpen = (open) => {
    const drawer = cartRoot();
    if (!drawer) return;
    if (open) {
      const search = searchRoot();
      if (search?.classList.contains("is-open")) {
        search.classList.remove("is-open");
        search.setAttribute("aria-hidden", "true");
        document.querySelector("[data-search-open]")?.setAttribute("aria-expanded", "false");
      }
    }
    drawer.classList.toggle("is-open", open);
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    const toggle = document.querySelector("[data-cart-open]");
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
    setInert(isOverlayOpen());
    if (open) drawer.querySelector("[data-cart-close]")?.focus();
  };

  const setSearchOpen = (open) => {
    const drawer = searchRoot();
    if (!drawer) return;
    if (open) {
      const cart = cartRoot();
      if (cart?.classList.contains("is-open")) {
        cart.classList.remove("is-open");
        cart.setAttribute("aria-hidden", "true");
        document.querySelector("[data-cart-open]")?.setAttribute("aria-expanded", "false");
      }
    }
    drawer.classList.toggle("is-open", open);
    drawer.setAttribute("aria-hidden", open ? "false" : "true");
    const toggle = document.querySelector("[data-search-open]");
    if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
    setInert(isOverlayOpen());
    if (open) {
      const input = drawer.querySelector("[data-search-input]");
      input?.focus();
      if (input && !input.value.trim()) renderSearchIdle();
    }
  };

  const searchStatus = () => searchRoot()?.querySelector("[data-search-status]");
  const searchResults = () => searchRoot()?.querySelector("[data-search-results]");

  const renderSearchIdle = () => {
    const status = searchStatus();
    const results = searchResults();
    if (status) status.textContent = strings.searchEmpty || "Start typing to search the desk.";
    if (results) results.innerHTML = "";
  };

  const productPriceCents = (product) => {
    const raw = product.price ?? product.price_min ?? product.price_max;
    if (raw == null) return null;
    if (typeof raw === "number") {
      return Number.isInteger(raw) && raw >= 1000 ? raw : Math.round(raw * 100);
    }
    const parsed = Number.parseFloat(String(raw).replace(/[^0-9.]/g, ""));
    if (Number.isNaN(parsed)) return null;
    return Math.round(parsed * 100);
  };

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const renderSearchProducts = (products, query) => {
    const status = searchStatus();
    const results = searchResults();
    if (!results) return;

    if (!products.length) {
      if (status) status.textContent = strings.searchNoResults || "No pieces matched.";
      results.innerHTML = "";
      return;
    }

    if (status) status.textContent = "";
    const items = products
      .map((product) => {
        const image = product.featured_image?.url || product.image || "";
        const cents = productPriceCents(product);
        const price = cents == null ? "" : formatMoney(cents);
        const title = escapeHtml(product.title || "");
        const url = escapeHtml(product.url || "#");
        const safeImage = escapeHtml(image);
        return `
          <a class="search-result" href="${url}">
            <span class="search-result__media">
              ${safeImage ? `<img src="${safeImage}" alt="" width="96" height="96" loading="lazy">` : ""}
            </span>
            <span class="search-result__copy">
              <span class="search-result__title">${title}</span>
              ${price ? `<span class="search-result__price">${escapeHtml(price)}</span>` : ""}
            </span>
          </a>
        `;
      })
      .join("");

    results.innerHTML = `
      ${items}
      <a class="btn btn--ghost search-drawer__all" href="${escapeHtml(searchPageUrl)}?q=${encodeURIComponent(query)}&type=product">
        ${escapeHtml(strings.searchViewAll || "View all results")}
      </a>
    `;
  };

  let searchTimer = 0;
  let searchAbort = null;

  const runPredictiveSearch = async (query) => {
    const status = searchStatus();
    const results = searchResults();
    if (!query) {
      renderSearchIdle();
      return;
    }
    if (status) status.textContent = strings.searchLoading || "Searching…";
    if (results) results.innerHTML = "";

    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();

    const base = String(predictiveUrl || `${root}search/suggest`).replace(/\.json$/i, "");
    const url = new URL(`${base}.json`, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", "8");
    url.searchParams.set("resources[options][unavailable_products]", "last");

    try {
      const response = await fetch(url.toString(), {
        signal: searchAbort.signal,
        headers: { Accept: "application/json" }
      });
      if (!response.ok) throw new Error("search failed");
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      renderSearchProducts(products, query);
    } catch (error) {
      if (error.name === "AbortError") return;
      if (status) status.textContent = strings.searchNoResults || "No pieces matched.";
      if (results) results.innerHTML = "";
    }
  };

  const updateCount = (count) => {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(count);
      el.hidden = count < 1;
    });
  };

  const refreshCart = async ({ open } = {}) => {
    const [sectionsRes, cartRes] = await Promise.all([
      fetch(`${root}?sections=cart-drawer`),
      fetch(`${root}cart.js`, { headers: { Accept: "application/json" } })
    ]);
    if (!sectionsRes.ok || !cartRes.ok) throw new Error(strings.cartUpdateError || "Cart could not be updated.");
    const sections = await sectionsRes.json();
    const cart = await cartRes.json();
    const wrapper = document.getElementById("shopify-section-cart-drawer");
    if (wrapper && sections["cart-drawer"]) wrapper.outerHTML = sections["cart-drawer"];
    updateCount(cart.item_count);
    if (open) setCartOpen(true);
    return cart;
  };

  const addVariant = async (id, quantity = 1) => {
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id, quantity }] })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || strings.addError || "Could not add this piece.");
    await refreshCart({ open: true });
  };

  const closeNavPanels = (except) => {
    document.querySelectorAll("[data-nav-item]").forEach((item) => {
      if (except && item === except) return;
      const trigger = item.querySelector("[data-nav-trigger]");
      const panel = item.querySelector("[data-nav-panel]");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (panel) panel.hidden = true;
      item.classList.remove("is-open");
    });
  };

  document.addEventListener("click", async (event) => {
    const navTrigger = event.target.closest("[data-nav-trigger]");
    if (navTrigger) {
      const item = navTrigger.closest("[data-nav-item]");
      const panel = item?.querySelector("[data-nav-panel]");
      if (!item || !panel) return;
      const willOpen = panel.hidden;
      closeNavPanels(item);
      panel.hidden = !willOpen;
      navTrigger.setAttribute("aria-expanded", willOpen ? "true" : "false");
      item.classList.toggle("is-open", willOpen);
      return;
    }

    if (!event.target.closest("[data-nav-item]")) {
      closeNavPanels();
    }

    const menuButton = event.target.closest("[data-menu-toggle]");
    if (menuButton) {
      const nav = document.querySelector("[data-mobile-nav]");
      if (!nav) return;
      const open = nav.hidden;
      nav.hidden = !open;
      menuButton.setAttribute("aria-expanded", open ? "true" : "false");
      const label = menuButton.querySelector("span");
      if (label) label.textContent = open ? strings.close || "Close" : strings.menu || "Menu";
      if (!open) closeNavPanels();
      setInert(open || cartRoot()?.classList.contains("is-open"));
      return;
    }

    if (event.target.closest("[data-cart-open]")) {
      setCartOpen(true);
      return;
    }

    if (event.target.closest("[data-cart-close]") || event.target.closest("[data-cart-backdrop]")) {
      setCartOpen(false);
      return;
    }

    if (event.target.closest("[data-search-open]")) {
      setSearchOpen(true);
      return;
    }

    if (event.target.closest("[data-search-close]")) {
      setSearchOpen(false);
      return;
    }

    if (event.target.closest("[data-announce-close]")) {
      try {
        sessionStorage.setItem("op-announce-closed", "1");
      } catch (error) {}
      document.documentElement.classList.add("announce-closed");
      return;
    }

    const qtyButton = event.target.closest("[data-qty-minus], [data-qty-plus]");
    if (qtyButton) {
      const input = qtyButton.parentElement.querySelector("input");
      if (!input) return;
      const next = Number(input.value || 1) + (qtyButton.hasAttribute("data-qty-plus") ? 1 : -1);
      input.value = String(Math.max(1, next));
      return;
    }

    const lineButton = event.target.closest("[data-line-qty]");
    if (lineButton) {
      const row = lineButton.closest("[data-line-key]");
      if (!row) return;
      const current = Number(row.querySelector("[data-line-qty-value]")?.textContent || 1);
      const next = lineButton.dataset.lineQty === "increase" ? current + 1 : current - 1;
      try {
        await fetch(`${root}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: row.dataset.lineKey, quantity: Math.max(0, next) })
        });
        await refreshCart({ open: true });
      } catch (error) {
        toast(strings.cartChangeError || "Could not update the cart.");
      }
    }

    const recover = event.target.closest("[data-open-recover]");
    if (recover) {
      event.preventDefault();
      const login = document.getElementById("login");
      const panel = document.getElementById("recover");
      if (login) login.hidden = true;
      if (panel) panel.hidden = false;
    }

    const back = event.target.closest("[data-close-recover]");
    if (back) {
      event.preventDefault();
      const login = document.getElementById("login");
      const panel = document.getElementById("recover");
      if (login) login.hidden = false;
      if (panel) panel.hidden = true;
    }

    const addressToggle = event.target.closest("[data-address-toggle]");
    if (addressToggle) {
      const panel = document.getElementById(`address-${addressToggle.dataset.addressToggle}`);
      if (panel) panel.hidden = !panel.hidden;
    }

    const thumb = event.target.closest("[data-thumb]");
    if (thumb) {
      const gallery = thumb.closest("[data-gallery]");
      showMedia(gallery, thumb.dataset.thumb);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    setCartOpen(false);
    setSearchOpen(false);
    closeNavPanels();
    const nav = document.querySelector("[data-mobile-nav]");
    if (nav && !nav.hidden) {
      nav.hidden = true;
      const menuButton = document.querySelector("[data-menu-toggle]");
      if (menuButton) {
        menuButton.setAttribute("aria-expanded", "false");
        const label = menuButton.querySelector("span");
        if (label) label.textContent = strings.menu || "Menu";
      }
      setInert(isOverlayOpen());
    }
  });

  document.addEventListener("input", (event) => {
    const input = event.target.closest("[data-search-input]");
    if (!input) return;
    const query = input.value.trim();
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      runPredictiveSearch(query);
    }, 220);
  });

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-auto-submit]")) {
      event.target.form?.submit();
      return;
    }
    if (!event.target.matches("[data-option]")) return;
    const form = event.target.closest("[data-product-form]");
    if (form) syncVariant(form);
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest("[data-product-form]");
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector("[data-add-button]");
    const error = form.querySelector("[data-product-error]");
    if (error) error.hidden = true;
    if (button) button.disabled = true;
    try {
      const id = form.querySelector('[name="id"]')?.value;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      await addVariant(id, quantity);
    } catch (error) {
      const slot = form.querySelector("[data-product-error]");
      if (slot) {
        slot.hidden = false;
        slot.textContent = error.message;
      } else {
        toast(error.message);
      }
    } finally {
      if (button) button.disabled = false;
    }
  });

  const showMedia = (gallery, id) => {
    if (!gallery || !id) return;
    gallery.querySelectorAll("[data-media-id]").forEach((el) => {
      el.hidden = el.dataset.mediaId !== String(id);
    });
    gallery.querySelectorAll("[data-thumb]").forEach((el) => {
      el.classList.toggle("is-active", el.dataset.thumb === String(id));
    });
  };

  const readVariants = (form) => {
    try {
      return JSON.parse(form.querySelector("[data-product-variants]")?.textContent || "[]");
    } catch (error) {
      return [];
    }
  };

  const syncVariant = (form) => {
    const variants = readVariants(form);
    const selected = [...form.querySelectorAll("[data-option]:checked")]
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map((input) => input.value);
    const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
    const button = form.querySelector("[data-add-button]");
    const price = form.querySelector("[data-price]");
    const compare = form.querySelector("[data-compare]");
    const sku = form.querySelector("[data-sku]");
    const idInput = form.querySelector('[name="id"]');
    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = strings.unavailable || "Unavailable";
      }
      return;
    }
    if (idInput) idInput.value = variant.id;
    if (price) price.textContent = formatMoney(variant.price);
    if (compare) {
      const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare.hidden = !showCompare;
      if (showCompare) compare.textContent = formatMoney(variant.compare_at_price);
    }
    if (sku) {
      sku.hidden = !variant.sku;
      sku.textContent = variant.sku ? `${strings.skuLabel || "SKU"} ${variant.sku}` : "";
    }
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? strings.addToCart || "Add to cart" : strings.soldOut || "Sold out";
    }
    if (variant.media_id) showMedia(form.closest(".product")?.querySelector("[data-gallery]"), variant.media_id);
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    history.replaceState({}, "", url);
  };

  if (window.location.hash === "#recover") {
    const login = document.getElementById("login");
    const panel = document.getElementById("recover");
    if (login) login.hidden = true;
    if (panel) panel.hidden = false;
  }

  const revealBands = () => {
    const nodes = document.querySelectorAll(".band--reveal");
    if (!nodes.length) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
  };
  revealBands();
})();
